import classNames from 'classnames'
import React, { FC, useCallback, useState } from 'react'
import InView from 'react-intersection-observer'
import { useTranslations } from '../../hooks/useTranslations'
import { InternalExternalLink } from '../../types/InternalExternalLink'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Button } from '../Button'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'
import { InteractionType, LinkWrapper } from '../LinkWrapper'
import { MaximizeButton } from '../MaximizeButton'
import { Paragraph } from '../Paragraph'
import { PauseButton } from '../PauseButton'
import { SoundButton } from '../SoundButton'
import { AspectRatio, VideoAspectRatioWrapper } from '../VideoAspectRatio'
import { VideoModal } from '../VideoModal'
import { VimeoVideo } from '../VimeoVideo'

export interface LargeTeaserProps {
  catchline1?: string
  catchline2?: string
  centerImageVertically?: boolean
  hideUnderline?: boolean
  image?: ImageProps
  link?: InternalExternalLink
  subtitle: string
  text: string
  title: string
  videoPreviewImage?: ImageProps
  videoUrl?: string
  videoControls?: boolean
}

export const LargeTeaser: FC<LargeTeaserProps> = ({
  catchline1,
  catchline2,
  centerImageVertically = false, // used on business line page
  hideUnderline = false,
  /** Optimal image format 5:4 */
  image,
  link,
  subtitle,
  text,
  title,
  /** Optimal video and preview image format 16:9 */
  videoPreviewImage,
  videoUrl,
  videoControls = false,
}) => {
  const translations = useTranslations()
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [started, setStarted] = useState(false)
  const [paused, setPaused] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(false)

  const onPause = useCallback(() => {
    setPaused(prev => !prev)
  }, [])

  const playVideo = () => {
    setVideoPlaying(true)
  }

  const catchlineCommonCss = classNames(
    'mb-5 font-t-bold text-t-300',
    'md:text-t-400',
    'lg:text-t-500'
  )

  const hasVideo = !isNullOrWhiteSpace(videoUrl)

  return (
    <Grid additionalClassName="relative">
      <div
        className={classNames(
          'col-span-full flex flex-col justify-start items-center text-center mb-60',
          'tb:col-start-1 tb:col-span-2 tb:row-start-1 tb:items-start tb:mb-0 tb:pr-45 tb:pl-30 tb:text-left',
          'sm:col-start-2 sm:col-span-2 sm:pl-0',
          'md:col-start-1 md:col-span-2 md:-mr-30',
          'lg:col-start-2 lg:col-span-3 lg:mr-0 lg:pr-90',
          'xl:pr-30',
          'xxl:col-start-3 xxl:col-span-3 xxl:mr-0 xxl:ml-0 xxl:pr-0',
          {
            'tb:pb-30 xl:pb-150 xxl:pb-330': !centerImageVertically && !hasVideo,
            'sm:-mr-30 sm:pr-30 md:pr-0': centerImageVertically,
            'sm:pr-0 xl:-ml-30': !centerImageVertically,
          }
        )}
      >
        {!isNullOrWhiteSpace(title) && (
          <h2 className="mb-15 text-primary font-h2 text-h2">{title}</h2>
        )}

        {!isNullOrWhiteSpace(subtitle) && (
          <h3
            className={classNames(
              'mb-15 max-w-240 font-t-bold text-t-600 text-secondary-darker',
              'tb:max-w-none tb:pr-30 tb:mb-30',
              'sm:pr-60 sm:mb-20',
              'md:mb-30',
              'lg:pr-0 lg:text-t-700',
              'xl:max-w-330 xl:mb-15 xl:text-t-800'
            )}
          >
            {subtitle}
          </h3>
        )}

        {!isNullOrWhiteSpace(text) && (
          <Paragraph
            className={classNames(
              'hidden font-t text-t-300 text-secondary-darkest last:mb-0',
              'tb:block tb:mb-20',
              'sm:text-t-400 sm:mb-30',
              'md:mb-15',
              'lg:text-t-600',
              'xl:text-t-700 xl:mb-30'
            )}
          >
            {text}
          </Paragraph>
        )}

        {(!isNullOrWhiteSpace(catchline1) || !isNullOrWhiteSpace(catchline2)) && (
          <div>
            {!isNullOrWhiteSpace(catchline1) && (
              <p className={classNames(catchlineCommonCss, 'text-primary')}>
                {catchline1}
              </p>
            )}
            {!isNullOrWhiteSpace(catchline2) && (
              <p className={classNames(catchlineCommonCss, 'text-secondary-dark')}>
                {catchline2}
              </p>
            )}
          </div>
        )}

        {link && !isNullOrWhiteSpace(link.url) && (
          <Button
            {...link}
            additionalClassName="mt-30 tb:mt-15 sm:mt-30"
            isExternalUrl={link.isExternal}
          >
            {link.title ?? translations.more}
          </Button>
        )}
      </div>

      {(!isNullOrWhiteSpace(videoUrl) || !!image) && (
        <div
          className={classNames(
            'col-span-full flex justify-center',
            centerImageVertically || hasVideo
              ? 'items-start tb:flex-col tb:h-full tb:justify-center sm:ml-0 sm:mr-30 xl:ml-0'
              : 'tb:justify-end tb:items-end sm:ml-120 xxl:ml-60',
            'tb:col-start-3 tb:col-span-2 tb:row-start-1 tb:-ml-30',
            'sm:col-start-4 sm:col-span-3',
            'md:ml-0 md:-mr-30',
            'lg:col-start-5 lg:ml-45 lg:-mr-75',
            'xl:col-start-6 xl:col-span-4',
            'xxl:col-start-8 xxl:col-span-6 xxl:mr-15'
          )}
        >
          {hasVideo ? (
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div
                  className="w-full relative max-w-[25.3125rem] md:max-w-[31.875rem] lg:max-w-[57.5rem]"
                  ref={ref}
                >
                  <VideoAspectRatioWrapper aspectRatio={AspectRatio['16/9']}>
                    <LinkWrapper
                      {...link}
                      additionalClassName="absolute inset-0"
                      additionalLinkClassName="focus:ring focus:ring-primary/40 focus:border-primary focus:outline-none"
                      fallbackElement="div"
                      interactionType={InteractionType.None}
                    >
                      {inView && (
                        <VimeoVideo
                          imgElementClassName="w-full h-full object-cover"
                          previewImage={videoPreviewImage}
                          title=""
                          videoUrl={videoUrl}
                          paused={!inView || paused} // keep paused until scrolled into view the first time
                          muted={!soundEnabled}
                          onBackgroundVideoStarted={() => setStarted(true)}
                        />
                      )}
                      {started && (
                        <div
                          className={classNames(
                            'absolute flex gap-15 z-10 top-0 right-0 m-15'
                          )}
                        >
                          <PauseButton onClick={onPause} />
                          {videoControls && (
                            <SoundButton
                              active={soundEnabled}
                              onClick={() => setSoundEnabled(old => !old)}
                            />
                          )}
                          {videoControls && <MaximizeButton onClick={playVideo} />}
                        </div>
                      )}
                    </LinkWrapper>
                  </VideoAspectRatioWrapper>
                </div>
              )}
            </InView>
          ) : (
            <>
              {!!image && !isNullOrWhiteSpace(image.src) && (
                <Image {...image} applyLqip imgClassName="w-full" loading="lazy" />
              )}
            </>
          )}
        </div>
      )}

      {!hideUnderline && (
        <hr
          className={classNames(
            'col-span-full row-start-auto border-neutral-lighter',
            'md:block md:-ml-30',
            {
              'mt-60 sm:mt-90 lg:mt-120': centerImageVertically,
            }
          )}
        />
      )}
      {hasVideo && videoControls && (
        <VideoModal
          onClose={() => setVideoPlaying(false)}
          open={videoPlaying}
          videoUrl={videoUrl}
        />
      )}
    </Grid>
  )
}
